import { useState, useEffect } from 'react';
import type { SpoolWithRelations } from '@maker-toolchest/shared';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { X, Scale } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';

interface WeightAdjustDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (remainingWeight: number) => Promise<void>;
  spool: SpoolWithRelations | null;
  loading?: boolean;
}

export function WeightAdjustDialog({
  open,
  onClose,
  onConfirm,
  spool,
  loading = false,
}: WeightAdjustDialogProps) {
  const [remainingWeight, setRemainingWeight] = useState<number>(0);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (open && spool) {
      setRemainingWeight(spool.remainingWeight);
      setError('');
    }
  }, [open, spool]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (remainingWeight < 0) {
      setError('Weight cannot be negative');
      return;
    }

    if (spool && remainingWeight > spool.initialWeight) {
      setError('Weight cannot exceed initial weight');
      return;
    }

    await onConfirm(remainingWeight);
  };

  const percentRemaining = spool && spool.initialWeight > 0
    ? Math.round((remainingWeight / spool.initialWeight) * 100)
    : 0;

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-xl">
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 rounded p-1 text-foreground-muted hover:bg-background-subtle hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Scale className="h-5 w-5 text-primary" />
                  </div>
                  <DialogTitle className="text-lg font-semibold text-foreground">
                    Update Weight
                  </DialogTitle>
                </div>

                {spool && (
                  <div className="mb-4 rounded-lg bg-background-subtle p-3">
                    <p className="text-sm text-foreground">
                      {spool.tenantFilament?.filament?.name}
                    </p>
                    <p className="text-xs text-foreground-muted">
                      Initial: {spool.initialWeight}g | Current: {spool.remainingWeight}g
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      label="Remaining Weight (g)"
                      type="number"
                      min={0}
                      max={spool?.initialWeight}
                      value={remainingWeight}
                      onChange={(e) => setRemainingWeight(parseFloat(e.target.value) || 0)}
                      error={error}
                    />

                    {/* Progress bar */}
                    <div className="mt-3 flex items-center gap-2">
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-background">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{ width: `${percentRemaining}%` }}
                        />
                      </div>
                      <span className="text-sm text-foreground-muted">{percentRemaining}%</span>
                    </div>
                  </div>

                  {/* Quick adjust buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setRemainingWeight(Math.max(0, remainingWeight - 50))}
                    >
                      -50g
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setRemainingWeight(Math.max(0, remainingWeight - 100))}
                    >
                      -100g
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setRemainingWeight(0)}
                    >
                      Empty
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setRemainingWeight(spool?.initialWeight || 0)}
                    >
                      Full
                    </Button>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
                      Cancel
                    </Button>
                    <Button type="submit" loading={loading}>
                      Update Weight
                    </Button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
